import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import TokenUsage from './TokenUsage';
import AITrackingWidget from './AITrackingWidget';
import ProductivityTimeline from './ProductivityTimeline';
import { personalizationService, PersonalizationSettings } from '../services/personalizationService';
import timezoneService from '../services/timezoneService';
import { DateService } from '../utils/dateService';
import { calculateTotalTime } from '../utils/durationCalculator';
import { calculateTopApps, calculateWorkTypes, calculateProjectWorkTypes } from '../utils/analyticsCalculator';
import { format } from 'date-fns-tz';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Chip,
  LinearProgress,
  IconButton,
} from '@mui/material';
import {
  Analytics,
  TrendingUp,
  Schedule,
  Computer,
  Work,
  Assessment,
  Speed,
  Timeline,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';

interface ProductivityData {
  date: string;
  totalHours: number;
  productiveHours: number;
  productivityScore: number;
  screenshotCount: number;
  topApps: { name: string; hours: number; productivity: 'high' | 'medium' | 'low' }[];
  workTypes: { type: string; hours: number; percentage: number }[];
  projectWorkTypes: { 
    projectName: string; 
    totalHours: number; 
    workTypes: { name: string; hours: number; percentage: number }[] 
  }[];
}

interface AIAnalyticsProps {}

const AIAnalytics: React.FC<AIAnalyticsProps> = () => {
  const [timeRange, setTimeRange] = useState('1d');
  const [selectedMetric, setSelectedMetric] = useState('productivity');
  const [analyticsData, setAnalyticsData] = useState<ProductivityData[]>([]);
  const [loading, setLoading] = useState(true);
  const [tokenStats, setTokenStats] = useState<any>(null);
  const [personalizationSettings, setPersonalizationSettings] = useState<PersonalizationSettings | null>(null);
  
  // Navigation state for different time periods - use user's current date in their timezone
  const [currentDate, setCurrentDate] = useState<Date>(() => {
    const todayString = timezoneService.getCurrentUserDate(); // Returns "YYYY-MM-DD"
    // eslint-disable-next-line no-restricted-syntax
    return new Date(todayString + 'T12:00:00.000Z'); // Parse as Date for React state (noon UTC to avoid timezone issues)
  });

  // Load personalization settings
  useEffect(() => {
    const loadPersonalization = async () => {
      try {
        const settings = await personalizationService.getPersonalizationSettings();
        setPersonalizationSettings(settings);
      } catch (error) {
        console.error('Failed to load personalization settings:', error);
      }
    };

    loadPersonalization();

    // Listen for personalization updates
    const handlePersonalizationUpdate = (event: CustomEvent<PersonalizationSettings>) => {
      setPersonalizationSettings(event.detail);
    };

    window.addEventListener('personalization-updated' as any, handlePersonalizationUpdate);
    
    return () => {
      window.removeEventListener('personalization-updated' as any, handlePersonalizationUpdate);
    };
  }, []);

  // Helper function to check if a date is in current week
  const isCurrentWeek = (date: Date, today: Date) => {
    const startOfWeek = new Date(today);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getUTCDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
    // Use timezone-safe date manipulation
    startOfWeek.setUTCDate(diff);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setUTCDate(startOfWeek.getUTCDate() + 6);
    
    return date >= startOfWeek && date <= endOfWeek;
  };

  // Load token stats from API
  const loadTokenStats = useCallback(async () => {
    try {
      const stats = await apiService.getTokenStats(timeRange);
      setTokenStats(stats);
    } catch (error) {
      console.error('Failed to load token stats:', error);
      setTokenStats({ estimatedCost: 0, totalRequests: 0 });
    }
  }, [timeRange]);

  // Load analytics data from API
  const loadAnalyticsData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('=== LOADING ANALYTICS DATA ===');
      console.log('timeRange:', timeRange);
      console.log('currentDate:', currentDate);
      
      // Load user's screenshot interval from AI settings
      let userInterval = 5;
      try {
        const settings = await apiService.getAISettings();
        userInterval = settings.data?.screenshotInterval || 5;
        console.log(`⚙️ AIAnalytics: Using interval ${userInterval} minutes`);
      } catch (error) {
        console.error('Failed to load interval, using default 5:', error);
      }
      
      // Build params to load screenshots directly
      const screenshotParams: Record<string, string> = {};
      
      if (timeRange === '1d') {
        // For daily view, use specific date in user's timezone
        const targetDate = currentDate || timezoneService.getCurrentUserDate();
        const dateKey = DateService.toStandardDate(targetDate);
        const { startUTC, endUTC } = DateService.userDateToUTCRange(dateKey);
        screenshotParams.startDate = startUTC;
        screenshotParams.endDate = endUTC;
      } else if (timeRange === '7d') {
        // For weekly view, calculate week range using DateService
        const targetDate = currentDate || timezoneService.getCurrentUserDate();
        const dateKey = DateService.toStandardDate(targetDate);
        // Get week start (Monday) and end (Sunday) from DateService
        const dayOfWeek = typeof targetDate === 'string' ? 
          // eslint-disable-next-line no-restricted-syntax
          new Date(targetDate).getDay() : targetDate.getDay();
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        // Calculate start of week by subtracting days
        // eslint-disable-next-line no-restricted-syntax
        const startOfWeekDate = new Date(typeof targetDate === 'string' ? targetDate : targetDate.toISOString());
        startOfWeekDate.setUTCDate(startOfWeekDate.getUTCDate() - daysToMonday);
        const startDateKey = DateService.toStandardDate(startOfWeekDate);
        // End of week is 6 days after start
        // eslint-disable-next-line no-restricted-syntax
        const endOfWeekDate = new Date(startOfWeekDate);
        endOfWeekDate.setUTCDate(endOfWeekDate.getUTCDate() + 6);
        const endDateKey = DateService.toStandardDate(endOfWeekDate);
        screenshotParams.startDate = startDateKey;
        screenshotParams.endDate = endDateKey;
      } else if (timeRange === '30d') {
        // For monthly view, calculate month range using DateService
        const targetDate = currentDate || timezoneService.getCurrentUserDate();
        const dateKey = DateService.toStandardDate(targetDate);
        // Parse date components
        const [year, month] = dateKey.split('-').map(Number);
        // First day of month
        const startDateKey = `${year}-${String(month).padStart(2, '0')}-01`;
        // Last day of month
        const daysInMonth = new Date(year, month, 0).getDate();
        const endDateKey = `${year}-${String(month).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
        screenshotParams.startDate = startDateKey;
        screenshotParams.endDate = endDateKey;
      }
      
      console.log('Screenshot params:', screenshotParams);
      
      // Load screenshots directly
      const screenshotsResponse = await apiService.getScreenshots(screenshotParams);
      const allScreenshots = screenshotsResponse.data;
      console.log('📸 AIAnalytics: Loaded screenshots from backend:', allScreenshots.length, `(${screenshotsResponse.pagination.total} total)`);
      
      // Filter screenshots by date range in user's timezone (frontend filtering)
      let startDateKey: string;
      let endDateKey: string;
      
      if (timeRange === '1d') {
        const targetDate = currentDate || timezoneService.getCurrentUserDate();
        startDateKey = DateService.toStandardDate(targetDate);
        endDateKey = startDateKey;
      } else if (timeRange === '7d') {
        const targetDate = currentDate || timezoneService.getCurrentUserDate();
        const dateKey = DateService.toStandardDate(targetDate);
        // Calculate week start and end using same logic as API calls
        const dayOfWeek = typeof targetDate === 'string' ? 
          // eslint-disable-next-line no-restricted-syntax
          new Date(targetDate).getDay() : targetDate.getDay();
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        // eslint-disable-next-line no-restricted-syntax
        const startOfWeekDate = new Date(typeof targetDate === 'string' ? targetDate : targetDate.toISOString());
        startOfWeekDate.setUTCDate(startOfWeekDate.getUTCDate() - daysToMonday);
        startDateKey = DateService.toStandardDate(startOfWeekDate);
        // eslint-disable-next-line no-restricted-syntax
        const endOfWeekDate = new Date(startOfWeekDate);
        endOfWeekDate.setUTCDate(endOfWeekDate.getUTCDate() + 6);
        endDateKey = DateService.toStandardDate(endOfWeekDate);
      } else if (timeRange === '30d') {
        const targetDate = currentDate || timezoneService.getCurrentUserDate();
        const dateKey = DateService.toStandardDate(targetDate);
        // Parse date components
        const [year, month] = dateKey.split('-').map(Number);
        startDateKey = `${year}-${String(month).padStart(2, '0')}-01`;
        // eslint-disable-next-line no-restricted-syntax
        const daysInMonth = new Date(year, month, 0).getDate();
        endDateKey = `${year}-${String(month).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
      } else {
        const today = timezoneService.getCurrentUserDate();
        startDateKey = DateService.toStandardDate(today);
        endDateKey = startDateKey;
      }
      
      const screenshots = allScreenshots.filter((screenshot: any) => {
        const zonedDate = timezoneService.convertUTCToUserTime(screenshot.timestamp);
        const dateKey = format(zonedDate, 'yyyy-MM-dd');
        return dateKey >= startDateKey && dateKey <= endDateKey;
      });
      
      console.log(`📸 AIAnalytics: After frontend filtering (${startDateKey} to ${endDateKey}): ${screenshots.length} screenshots`);
      
      // Sort screenshots chronologically before gap detection (required by sorting rules)
      screenshots.sort((a: any, b: any) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      
      // Calculate total time with gap detection
      const totalTimeMinutes = calculateTotalTime(screenshots, userInterval);
      const totalHours = totalTimeMinutes / 60;
      
      // Calculate productive time (screenshots with high productivity)
      const productiveScreenshots = screenshots.filter((s: any) => {
        const productivity = s.productivity?.toLowerCase() || 'medium';
        // Consider high and medium productivity as productive
        return productivity === 'high' || productivity === 'medium';
      });
      
      // Sort productive screenshots chronologically
      productiveScreenshots.sort((a: any, b: any) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      
      const productiveTimeMinutes = calculateTotalTime(productiveScreenshots, userInterval);
      const productiveHours = productiveTimeMinutes / 60;
      
      // Calculate productivity score
      const productivityScore = totalHours > 0 ? Math.round((productiveHours / totalHours) * 100) : 0;
      
      console.log(`⏱️ AIAnalytics: Total time: ${totalTimeMinutes} min (${totalHours.toFixed(2)}h)`);
      console.log(`⏱️ AIAnalytics: Productive time: ${productiveTimeMinutes} min (${productiveHours.toFixed(2)}h)`);
      console.log(`📊 AIAnalytics: Productivity score: ${productivityScore}%`);
      
      // ARCHITECTURE FIX: Calculate ALL analytics in frontend using new analyticsCalculator
      console.log('📊 AIAnalytics: Calculating analytics in frontend...');
      
      const topApps = calculateTopApps(screenshots, userInterval);
      const workTypes = calculateWorkTypes(screenshots, userInterval);
      const projectWorkTypes = calculateProjectWorkTypes(screenshots, userInterval);
      
      console.log('📊 Frontend calculated topApps:', topApps.length);
      console.log('📊 Frontend calculated workTypes:', workTypes.length);
      console.log('📊 Frontend calculated projectWorkTypes:', projectWorkTypes.length);
      
      // Transform to component format - ALL calculations from frontend
      const transformedData: ProductivityData[] = [{
        date: DateService.getToday(),
        totalHours: totalHours,
        productiveHours: productiveHours,
        productivityScore: productivityScore,
        screenshotCount: screenshots.length,
        topApps: topApps.map(app => ({
          name: app.name,
          hours: app.hours,
          productivity: app.productivity || 'medium'
        })),
        workTypes: workTypes.map(type => ({
          type: type.name,
          hours: type.hours,
          percentage: type.percentage
        })),
        projectWorkTypes: projectWorkTypes
      }];
      
      console.log('=== TRANSFORMED DATA ===');
      console.log('transformedData:', transformedData);
      
      setAnalyticsData(transformedData);
      
      console.log('=== DATA SET SUCCESSFULLY ===');
    } catch (error) {
      console.error('=== API ERROR ===');
      console.error('Error:', error);
      console.error('Error type:', typeof error);
      console.error('Error message:', error instanceof Error ? error.message : 'Unknown error');
      
      // Show empty data on error
      const errorData: ProductivityData[] = [{
        date: DateService.getToday(),
        totalHours: 0,
        productiveHours: 0,
        productivityScore: 0,
        screenshotCount: 0,
        topApps: [],
        workTypes: [],
        projectWorkTypes: []
      }];
      
      setAnalyticsData(errorData);
      console.log('=== SET EMPTY ERROR DATA ===');
    } finally {
      setLoading(false);
      console.log('=== LOADING FINISHED ===');
    }
  }, [timeRange, currentDate]);

  // Handle timeRange change
  const handleTimeRangeChange = async (newTimeRange: string) => {
    console.log('=== TIME RANGE CHANGE ===');
    console.log('Old timeRange:', timeRange);
    console.log('New timeRange:', newTimeRange);
    
    // Reset to today when changing time range - use user's timezone
    const todayString = timezoneService.getCurrentUserDate(); // Returns "YYYY-MM-DD"
    // eslint-disable-next-line no-restricted-syntax
    const today = new Date(todayString + 'T12:00:00.000Z'); // Parse as Date for React state
    setCurrentDate(today);
    setTimeRange(newTimeRange);
    // loadAnalyticsData will be called automatically via useEffect
  };

  // Navigation functions - use new Date() for cloning React state
  const navigatePrevious = () => {
    // eslint-disable-next-line no-restricted-syntax
    const newDate = new Date(currentDate);
    
    if (timeRange === '1d') {
      // Go to previous day - use timezone-safe date manipulation
      newDate.setUTCDate(newDate.getUTCDate() - 1);
    } else if (timeRange === '7d') {
      // Go to previous week - use timezone-safe date manipulation
      newDate.setUTCDate(newDate.getUTCDate() - 7);
    } else if (timeRange === '30d') {
      // Go to previous month - use timezone-safe date manipulation
      newDate.setUTCMonth(newDate.getUTCMonth() - 1);
    }
    
    setCurrentDate(newDate);
  };

  const navigateNext = () => {
    // eslint-disable-next-line no-restricted-syntax
    const newDate = new Date(currentDate);
    // eslint-disable-next-line no-restricted-syntax
    const today = timezoneService.convertUTCToUserTime(new Date().toISOString());
    
    if (timeRange === '1d') {
      // Go to next day (but not beyond today) - use timezone-safe date manipulation
      newDate.setUTCDate(newDate.getUTCDate() + 1);
      if (newDate <= today) {
        setCurrentDate(newDate);
      }
    } else if (timeRange === '7d') {
      // Go to next week (but not beyond current week) - use timezone-safe date manipulation
      newDate.setUTCDate(newDate.getUTCDate() + 7);
      if (newDate <= today) {
        setCurrentDate(newDate);
      }
    } else if (timeRange === '30d') {
      // Go to next month (but not beyond current month) - use timezone-safe date manipulation
      newDate.setUTCMonth(newDate.getUTCMonth() + 1);
      if (newDate <= today) {
        setCurrentDate(newDate);
      }
    }
  };

  const canNavigateNext = () => {
    const testDate = new Date(currentDate);
    const today = timezoneService.convertUTCToUserTime(new Date().toISOString());
    
    // Set time to start of day for accurate comparison - use timezone-safe methods
    today.setUTCHours(0, 0, 0, 0);
    testDate.setUTCHours(0, 0, 0, 0);
    
    if (timeRange === '1d') {
      // Use timezone-safe date manipulation
      testDate.setUTCDate(testDate.getUTCDate() + 1);
      return testDate.getTime() <= today.getTime();
    } else if (timeRange === '7d') {
      // Use timezone-safe date manipulation
      testDate.setUTCDate(testDate.getUTCDate() + 7);
      return testDate.getTime() <= today.getTime();
    } else if (timeRange === '30d') {
      // Use timezone-safe date manipulation
      testDate.setUTCMonth(testDate.getUTCMonth() + 1);
      return testDate.getTime() <= today.getTime();
    }
    
    return false;
  };

  const getDateRangeText = () => {
    if (timeRange === '1d') {
      // Always show weekday for day mode
      return timezoneService.formatUserDate(currentDate, { 
        weekday: 'long'
      });
    } else if (timeRange === '7d') {
      // Calculate week range - use timezone-safe date manipulation
      const startOfWeek = new Date(currentDate);
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getUTCDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
      startOfWeek.setUTCDate(diff);
      
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setUTCDate(startOfWeek.getUTCDate() + 6);
      
      const today = timezoneService.convertUTCToUserTime(new Date().toISOString());
      const isCurrentWeek = startOfWeek <= today && today <= endOfWeek;
      
      if (isCurrentWeek) {
        return 'This Week';
      } else {
        return `${startOfWeek.getDate()} - ${endOfWeek.getDate()} ${timezoneService.formatUserDate(startOfWeek, { month: 'short' })}`;
      }
    } else if (timeRange === '30d') {
      const today = timezoneService.convertUTCToUserTime(new Date().toISOString());
      const isCurrentMonth = currentDate.getUTCMonth() === today.getUTCMonth() && currentDate.getUTCFullYear() === today.getUTCFullYear();
      
      if (isCurrentMonth) {
        return 'This Month';
      } else {
        return timezoneService.formatUserDate(currentDate, { 
          year: 'numeric', 
          month: 'long' 
        });
      }
    }
    
    return '';
  };

  // Load data when component mounts
  useEffect(() => {
    loadTokenStats();
  }, [loadTokenStats]);

  // Load analytics when component mounts or when dependencies change
  // loadAnalyticsData automatically re-runs when timeRange or currentDate changes (via useCallback dependencies)
  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  // Also reload when selectedMetric changes (for future filtering)
  useEffect(() => {
    console.log('Selected metric changed to:', selectedMetric);
    // This will trigger a re-render with different focus
  }, [selectedMetric]);

  const getCurrentData = () => {
    if (analyticsData.length === 0) return null;
    return analyticsData[0]; // Most recent day
  };

  const getProductivityColor = (productivity: string) => {
    switch (productivity) {
      case 'high': return '#27ae60';
      case 'medium': return '#f39c12';
      case 'low': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  const getProductivityLabel = (productivity: string) => {
    switch (productivity) {
      case 'high': return 'High Productivity';
      case 'medium': return 'Medium Productivity';
      case 'low': return 'Low Productivity';
      default: return 'Unknown';
    }
  };

  const formatHours = (hours: number) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const formatCost = (cost: number) => {
    return `$${cost.toFixed(2)}`;
  };

  const currentData = getCurrentData();

  // Get brand-adapted colors
  const getBrandColor = () => {
    return personalizationSettings?.theme.primaryColor || '#64748b'; // Neutral gray as fallback
  };

  const getBrandAccentColor = () => {
    return personalizationSettings?.theme.accentColor || '#94a3b8'; // Lighter neutral as fallback
  };

  const getBrandTextColor = () => {
    return personalizationSettings?.theme.textColor || '#1a1a1a';
  };

  const getBrandBackgroundColor = () => {
    return personalizationSettings?.theme.backgroundColor || '#ffffff';
  };

  const getBrandSurfaceColor = () => {
    return personalizationSettings?.theme.surfaceColor || '#f8f9fa';
  };

  const getBrandSecondaryTextColor = () => {
    return '#666'; // Keep neutral for readability
  };

  const getBrandBorderColor = () => {
    return '#e9ecef'; // Keep neutral for subtle borders
  };

  const getBrandSuccessColor = () => {
    return '#27ae60'; // Keep green for success states
  };

  const getBrandWarningColor = () => {
    return getBrandAccentColor(); // Use brand accent for warnings
  };

  const getBrandErrorColor = () => {
    return '#e74c3c'; // Keep red for errors
  };

  // Get focused insights based on selected metric
  const getFocusedInsights = () => {
    if (!currentData || currentData.screenshotCount === 0) {
      return (
        <div style={styles.insightCard}>
          <Typography sx={styles.insightTitle}>📊 No Data Available</Typography>
          <Typography sx={styles.insightText}>
            Start tracking screenshots to see AI-powered insights about your productivity patterns and work habits. 
            The AI will analyze your screen activity to provide personalized {selectedMetric} recommendations.
          </Typography>
        </div>
      );
    }

    switch (selectedMetric) {
      case 'productivity':
        return (
          <>
            <div style={styles.insightCard}>
              <Typography sx={styles.insightTitle}>🎯 Productivity Focus</Typography>
              <Typography sx={styles.insightText}>
                Your productivity score of {currentData.productivityScore}% is {
                  currentData.productivityScore >= 90 ? 'excellent! You\'re in a highly productive flow state.' :
                  currentData.productivityScore >= 75 ? 'very good. You\'re maintaining strong focus on productive tasks.' :
                  currentData.productivityScore >= 60 ? 'decent, but there\'s room for improvement in task focus.' :
                  'below average. Consider reviewing your work patterns and eliminating distractions.'
                }
              </Typography>
            </div>
            
            <div style={styles.insightCard}>
              <Typography sx={styles.insightTitle}>📈 Productivity Optimization</Typography>
              <Typography sx={styles.insightText}>
                {currentData.productivityScore >= 90 ? 'Keep up the excellent work! Your current workflow is highly effective. Consider documenting your successful patterns.' :
                 currentData.productivityScore >= 75 ? 'Consider time-blocking to maintain your good productivity levels throughout the day.' :
                 currentData.productivityScore >= 60 ? 'Try the Pomodoro technique (25min work + 5min break) to improve focus and reduce distractions.' :
                 'Focus on eliminating distractions and creating dedicated work blocks. Consider using website blockers during work hours.'}
              </Typography>
            </div>

            <div style={styles.insightCard}>
              <Typography sx={styles.insightTitle}>⚡ Performance Insights</Typography>
              <Typography sx={styles.insightText}>
                Based on {currentData.screenshotCount} screenshots, your productivity ratio is {Math.round((currentData.productiveHours / currentData.totalHours) * 100)}%. 
                {currentData.topApps.length > 0 && ` Your most productive tool is ${currentData.topApps[0].name} with ${currentData.topApps[0].productivity} productivity rating.`}
              </Typography>
            </div>
          </>
        );

      case 'focus':
        return (
          <>
            <div style={styles.insightCard}>
              <Typography sx={styles.insightTitle}>🧠 Focus Analysis</Typography>
              <Typography sx={styles.insightText}>
                You've maintained focus for {formatHours(currentData.productiveHours)} out of {formatHours(currentData.totalHours)} total work time. 
                This represents a {Math.round((currentData.productiveHours / currentData.totalHours) * 100)}% focus rate, which is {
                  (currentData.productiveHours / currentData.totalHours) >= 0.8 ? 'excellent for deep work!' :
                  (currentData.productiveHours / currentData.totalHours) >= 0.6 ? 'good, but could be improved.' :
                  'below optimal - consider focus techniques.'
                }
              </Typography>
            </div>

            <div style={styles.insightCard}>
              <Typography sx={styles.insightTitle}>⏰ Focus Time Patterns</Typography>
              <Typography sx={styles.insightText}>
                Your focus sessions average {Math.round((currentData.productiveHours * 60) / currentData.screenshotCount)} minutes per productive period. 
                {(currentData.productiveHours * 60) / currentData.screenshotCount >= 25 ? 
                  ' This suggests good sustained attention spans.' :
                  ' Consider longer focus blocks - aim for 25-50 minute sessions for optimal concentration.'
                }
              </Typography>
            </div>

            <div style={styles.insightCard}>
              <Typography sx={styles.insightTitle}>🎯 Focus Recommendations</Typography>
              <Typography sx={styles.insightText}>
                {(currentData.productiveHours / currentData.totalHours) >= 0.8 ? 
                  'Your focus is excellent! Maintain this by taking regular breaks and staying hydrated.' :
                  'To improve focus: 1) Use the 2-minute rule for small tasks, 2) Batch similar activities, 3) Set specific focus goals for each session, 4) Minimize notifications during work blocks.'
                }
              </Typography>
            </div>
          </>
        );

      case 'apps':
        return (
          <>
            <div style={styles.insightCard}>
              <Typography sx={styles.insightTitle}>💻 Application Usage Analysis</Typography>
              <Typography sx={styles.insightText}>
                You've used {currentData.topApps.length} different applications, with {currentData.topApps[0]?.name || 'Unknown'} being your primary tool ({formatHours(currentData.topApps[0]?.hours || 0)}). 
                {currentData.topApps.filter(app => app.productivity === 'high').length > 0 ? 
                  ` You have ${currentData.topApps.filter(app => app.productivity === 'high').length} high-productivity applications in your workflow.` :
                  ' Consider identifying which tools boost your productivity most.'
                }
              </Typography>
            </div>

            <div style={styles.insightCard}>
              <Typography sx={styles.insightTitle}>🔧 Tool Optimization</Typography>
              <Typography sx={styles.insightText}>
                {currentData.topApps.length <= 3 ? 
                  'You maintain good focus with minimal app switching. This concentrated approach is excellent for deep work.' :
                  currentData.topApps.length <= 6 ?
                  'Moderate app usage detected. Consider if all tools are necessary for your current tasks.' :
                  'High app switching detected. This might indicate task fragmentation - consider batching similar activities.'
                }
              </Typography>
            </div>

            <div style={styles.insightCard}>
              <Typography sx={styles.insightTitle}>📊 Productivity by Application</Typography>
              <Typography sx={styles.insightText}>
                {currentData.topApps.filter(app => app.productivity === 'high').length > 0 ? 
                  `High-productivity apps: ${currentData.topApps.filter(app => app.productivity === 'high').map(app => app.name).join(', ')}. Focus more time on these tools.` :
                  'No high-productivity applications identified. Review your tool usage and identify which applications contribute most to your goals.'
                }
              </Typography>
            </div>
          </>
        );

      case 'worktype':
        return (
          <>
            <div style={styles.insightCard}>
              <Typography sx={styles.insightTitle}>🔧 Work Type Analysis</Typography>
              <Typography sx={styles.insightText}>
                Your primary work type is {currentData.workTypes[0]?.type || 'Unknown'} ({currentData.workTypes[0]?.percentage || 0}% of time). 
                {currentData.workTypes[0]?.type === 'Development' ? ' You\'re spending most time on coding and development work - great for building technical skills.' :
                 currentData.workTypes[0]?.type === 'Design' ? ' You\'re focused on creative and design tasks - excellent for visual problem-solving.' :
                 currentData.workTypes[0]?.type === 'Communication' ? ' You\'re spending significant time on meetings and communication - important for collaboration.' :
                 ' This represents your main work activity pattern.'}
              </Typography>
            </div>

            <div style={styles.insightCard}>
              <Typography sx={styles.insightTitle}>⚖️ Work Balance</Typography>
              <Typography sx={styles.insightText}>
                {currentData.workTypes.length === 1 ? 
                  'You\'re highly focused on one work type. This deep specialization can be very effective, but consider if you need variety for skill development.' :
                  currentData.workTypes.length <= 3 ?
                  `You balance ${currentData.workTypes.length} work types effectively. This variety can prevent monotony and develop diverse skills.` :
                  'You\'re juggling many different work types. Consider if this variety is intentional or if you need better task organization.'
                }
              </Typography>
            </div>

            <div style={styles.insightCard}>
              <Typography sx={styles.insightTitle}>📈 Work Type Optimization</Typography>
              <Typography sx={styles.insightText}>
                {currentData.workTypes[0]?.percentage >= 80 ? 
                  'High concentration in one work type. Consider scheduling dedicated time blocks for different activities to maintain engagement.' :
                  currentData.workTypes[0]?.percentage >= 60 ?
                  'Good primary focus with some variety. This balance often leads to sustained productivity.' :
                  'Well-distributed work types. Ensure transitions between different work modes are intentional and not due to distractions.'
                }
              </Typography>
            </div>
          </>
        );

      default:
        return (
          <div style={styles.insightCard}>
            <Typography sx={styles.insightTitle}>📊 General Analysis</Typography>
            <Typography sx={styles.insightText}>
              Select a focus metric above to see specialized insights for that area.
            </Typography>
          </div>
        );
    }
  };

  const styles = {
    container: {
      backgroundColor: 'white',
      minHeight: '100vh',
      padding: '24px'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '32px'
    },
    title: {
      fontSize: '28px',
      fontWeight: '600',
      color: getBrandTextColor()
    },
    subtitle: {
      fontSize: '16px',
      color: getBrandSecondaryTextColor(),
      marginBottom: '24px'
    },
    metricsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '24px',
      marginBottom: '32px'
    },
    metricsGridSmall: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '16px',
      marginBottom: '24px'
    },
    metricCard: {
      padding: '24px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      height: '200px', // Fixed height for consistent card sizes
      display: 'flex',
      flexDirection: 'column'
    },
    metricHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '16px'
    },
    metricHeaderLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    metricTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: getBrandTextColor()
    },
    metricValue: {
      fontSize: '32px',
      fontWeight: '700',
      color: getBrandColor(),
      marginBottom: '8px'
    },
    metricSubtext: {
      fontSize: '14px',
      color: getBrandSecondaryTextColor()
    },
    progressContainer: {
      marginTop: '16px'
    },
    progressLabel: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '8px'
    },
    chartsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
      gap: '24px'
    },
    chartCard: {
      padding: '24px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    chartTitle: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    chartTitleLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    appItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 0',
      borderBottom: '1px solid #f0f0f0'
    },
    appInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    appName: {
      fontSize: '14px',
      fontWeight: '500'
    },
    appHours: {
      fontSize: '14px',
      color: '#666'
    },
    workTypeItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px'
    },
    workTypeInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    workTypeBar: {
      width: '100%',
      height: '8px',
      backgroundColor: '#f0f0f0',
      borderRadius: '4px',
      overflow: 'hidden',
      marginTop: '8px'
    },
    insightCard: {
      backgroundColor: '#f8f9fa',
      border: '1px solid #e9ecef',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px'
    },
    insightTitle: {
      fontSize: '14px',
      fontWeight: '600',
      marginBottom: '8px',
      color: '#495057'
    },
    insightText: {
      fontSize: '13px',
      color: '#6c757d',
      lineHeight: '1.4'
    },
    dropdown: {
      padding: '6px 10px',
      border: '1px solid #ccc',
      borderRadius: '4px',
      fontSize: '12px',
      backgroundColor: 'white',
      minWidth: '120px'
    },
    dropdownActive: {
      padding: '6px 10px',
      border: `2px solid ${getBrandColor()}`,
      borderRadius: '4px',
      fontSize: '12px',
      backgroundColor: '#f8f9fa',
      minWidth: '120px'
    }
  };

  if (!currentData) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <Analytics sx={{ fontSize: '32px', color: getBrandColor() }} />
          <h1 style={styles.title}>AI Analytics (Smart Hybrid)</h1>
        </div>
        <Typography>Loading analytics data...</Typography>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <Analytics sx={{ fontSize: '32px', color: getBrandColor() }} />
        <h1 style={styles.title}>AI Analytics</h1>
      </div>
      
      <p style={styles.subtitle}>
        Analyze your productivity patterns and work habits with AI-powered insights.
        {currentData.totalHours === 0 && currentData.topApps.length === 0 && (
          <span style={{ color: getBrandErrorColor(), fontWeight: 'bold' }}>
            <br />📊 No data available for selected time range. Start tracking screenshots to see analytics!
          </span>
        )}
      </p>

      {/* Key Metrics with AI Tracking - 3 cards in a row */}
      <div style={styles.metricsGridSmall}>
        {/* AI Tracking Control - Moved to left */}
        <AITrackingWidget />
        <Card sx={styles.metricCard}>
          <div style={styles.metricHeader}>
            <div style={styles.metricHeaderLeft}>
              <Timeline sx={{ fontSize: '20px', color: getBrandColor() }} />
              <Typography sx={{ fontSize: '16px', fontWeight: '600', color: getBrandTextColor() }}>Activity Timeline</Typography>
              <Typography variant="body2" sx={{ 
                color: getBrandSecondaryTextColor(), 
                fontWeight: 500,
                marginLeft: '8px'
              }}>
                Productivity patterns over time
              </Typography>
            </div>
            
            {/* Navigation Controls in Activity Timeline Header - Always visible */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={navigatePrevious}
                sx={{ 
                  width: 24, 
                  height: 24,
                  color: getBrandColor(),
                  '&:hover': { backgroundColor: getBrandSurfaceColor() }
                }}
              >
                <ChevronLeft sx={{ fontSize: 16 }} />
              </IconButton>
              
              <Typography sx={{ 
                fontSize: '11px', 
                fontWeight: '600', 
                color: getBrandTextColor(),
                minWidth: '60px',
                textAlign: 'center',
                px: 0.5
              }}>
                {getDateRangeText()}
              </Typography>
              
              <IconButton
                size="small"
                onClick={navigateNext}
                disabled={!canNavigateNext()}
                sx={{ 
                  width: 24, 
                  height: 24,
                  color: canNavigateNext() ? getBrandColor() : '#ccc',
                  '&:hover': { backgroundColor: canNavigateNext() ? getBrandSurfaceColor() : 'transparent' },
                  '&:disabled': { color: '#ccc' }
                }}
              >
                <ChevronRight sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </div>
          
          {/* Timeline component - proper container with overflow handling */}
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center',
            minHeight: '120px',
            overflow: 'hidden',
            marginTop: '8px',
            marginBottom: '8px'
          }}>
            <ProductivityTimeline timeRange={timeRange} currentDate={currentDate} />
          </Box>
          
          {/* Simple bottom info */}
          {currentData.screenshotCount > 0 && (
            <Typography sx={{ 
              fontSize: '11px', 
              color: '#666', 
              marginTop: '12px',
              textAlign: 'center'
            }}>
              {currentData.screenshotCount} data points • {Math.round((currentData.productiveHours / currentData.totalHours) * 100) || 0}% productive
            </Typography>
          )}
        </Card>

        <Card sx={styles.metricCard}>
          <div style={styles.metricHeader}>
            <div style={styles.metricHeaderLeft}>
              <Schedule sx={{ fontSize: '20px', color: getBrandColor() }} />
              <Typography sx={{ fontSize: '16px', fontWeight: '600', color: getBrandTextColor() }}>Total Work Time</Typography>
            </div>
            <select
              value={timeRange}
              onChange={(e) => handleTimeRangeChange(e.target.value)}
              style={loading ? styles.dropdownActive : styles.dropdown}
            >
              <option value="1d">Day</option>
              <option value="7d">Week</option>
              <option value="30d">Month</option>
            </select>
          </div>
          <Typography sx={{ fontSize: '24px', fontWeight: '700', color: getBrandColor(), marginBottom: '8px' }}>
            {formatHours(currentData.totalHours)}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: getBrandSecondaryTextColor() }}>
            {formatHours(currentData.productiveHours)} productive time
            {loading && <span style={{ color: getBrandColor(), marginLeft: '8px' }}>⏳ Loading...</span>}
          </Typography>
          <div style={styles.progressContainer}>
            <div style={styles.progressLabel}>
              <Typography sx={{ fontSize: '11px', color: getBrandSecondaryTextColor() }}>Productive vs Total</Typography>
              <Typography sx={{ fontSize: '11px', color: getBrandSecondaryTextColor() }}>
                {currentData.totalHours > 0 ? Math.round((currentData.productiveHours / currentData.totalHours) * 100) : 0}%
              </Typography>
            </div>
            <LinearProgress
              variant="determinate"
              value={currentData.totalHours > 0 ? (currentData.productiveHours / currentData.totalHours) * 100 : 0}
              sx={{
                height: '8px',
                borderRadius: '4px',
                backgroundColor: '#f0f0f0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#27ae60'
                }
              }}
            />
          </div>
        </Card>
      </div>

      {/* Charts and Analysis */}
      <div style={styles.chartsGrid}>
        {/* Top Applications */}
        <Card sx={styles.chartCard}>
          <div style={styles.chartTitle}>
            <div style={styles.chartTitleLeft}>
              <Computer sx={{ fontSize: '20px' }} />
              <Typography sx={{ fontSize: '18px', fontWeight: '600' }}>Top Applications</Typography>
            </div>
          </div>
          {currentData.topApps.map((app, index) => (
            <div key={app.name} style={styles.appItem}>
              <div style={styles.appInfo}>
                <Typography sx={styles.appName}>{app.name}</Typography>
                <Chip
                  label={getProductivityLabel(app.productivity)}
                  size="small"
                  sx={{
                    backgroundColor: getProductivityColor(app.productivity),
                    color: 'white',
                    fontSize: '10px'
                  }}
                />
              </div>
              <Typography sx={styles.appHours}>{formatHours(app.hours)}</Typography>
            </div>
          ))}
        </Card>

        {/* Work Type Distribution - Nested by Project */}
        <Card sx={styles.chartCard}>
          <div style={styles.chartTitle}>
            <div style={styles.chartTitleLeft}>
              <Work sx={{ fontSize: '20px' }} />
              <Typography sx={{ fontSize: '18px', fontWeight: '600' }}>Work Type Distribution</Typography>
            </div>
          </div>
          {currentData.projectWorkTypes && currentData.projectWorkTypes.length > 0 ? (
            currentData.projectWorkTypes.map((project) => (
              <div key={project.projectName} style={{ marginBottom: '20px' }}>
                {/* Project Header */}
                <Typography sx={{ fontSize: '15px', fontWeight: '600', color: getBrandTextColor(), marginBottom: '8px' }}>
                  📁 {project.projectName} - {formatHours(project.totalHours)}
                </Typography>
                
                {/* WorkTypes under this project */}
                {project.workTypes.map((workType) => (
                  <div key={workType.name} style={{ ...styles.workTypeItem, marginBottom: '12px', marginLeft: '16px' }}>
                    <div style={styles.workTypeInfo}>
                      <Typography sx={{ ...styles.appName, fontSize: '13px' }}>{workType.name}</Typography>
                      <Typography sx={{ ...styles.appHours, fontSize: '13px' }}>
                        {formatHours(workType.hours)} ({workType.percentage}%)
                      </Typography>
                    </div>
                    <div style={{ ...styles.workTypeBar, width: '100px' }}>
                      <div
                        style={{
                          width: `${workType.percentage}%`,
                          height: '100%',
                          backgroundColor: getBrandColor()
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ))
          ) : (
            // Fallback to old flat structure if no project data
            currentData.workTypes.map((workType, index) => (
              <div key={workType.type} style={styles.workTypeItem}>
                <div style={styles.workTypeInfo}>
                  <Typography sx={styles.appName}>{workType.type}</Typography>
                  <Typography sx={styles.appHours}>
                    {formatHours(workType.hours)} ({workType.percentage}%)
                  </Typography>
                </div>
                <div style={{ ...styles.workTypeBar, width: '120px' }}>
                  <div
                    style={{
                      width: `${workType.percentage}%`,
                      height: '100%',
                      backgroundColor: getBrandColor()
                    }}
                  />
                </div>
              </div>
            ))
          )}
        </Card>

        {/* AI Insights - Now Dynamic Based on Selected Metric */}
        <Card sx={styles.chartCard}>
          <div style={styles.chartTitle}>
            <div style={styles.chartTitleLeft}>
              <Assessment sx={{ fontSize: '20px' }} />
              <Typography sx={{ fontSize: '18px', fontWeight: '600' }}>
                AI Insights - {selectedMetric === 'productivity' ? 'Productivity Focus' : 
                              selectedMetric === 'focus' ? 'Focus Analysis' :
                              selectedMetric === 'apps' ? 'Application Analysis' :
                              'Work Type Analysis'}
              </Typography>
            </div>
            <select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              style={selectedMetric !== 'productivity' ? styles.dropdownActive : styles.dropdown}
            >
              <option value="productivity">Productivity</option>
              <option value="focus">Focus Time</option>
              <option value="apps">Applications</option>
              <option value="worktype">Work Types</option>
            </select>
          </div>
          
          {getFocusedInsights()}
        </Card>

        {/* Data Summary & Token Usage */}
        <Card sx={styles.chartCard}>
          <div style={styles.chartTitle}>
            <div style={styles.chartTitleLeft}>
              <Timeline sx={{ fontSize: '20px' }} />
              <Typography sx={{ fontSize: '18px', fontWeight: '600' }}>Data Summary & Token Usage</Typography>
            </div>
          </div>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>Current Analytics Status</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Screenshots:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {currentData.screenshotCount > 0 ? `${currentData.screenshotCount} analyzed` : '0 available'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Applications tracked:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {currentData.topApps.length}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Work types:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {currentData.workTypes.length}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Time range:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {timeRange === '1d' ? 'Today' : timeRange === '7d' ? 'This Week' : 'This Month'}
                  {loading && <span style={{ color: getBrandColor(), marginLeft: '8px' }}>⏳</span>}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Focus metric:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: selectedMetric !== 'productivity' ? getBrandColor() : 'inherit' }}>
                  {selectedMetric === 'productivity' ? 'Productivity Score' :
                   selectedMetric === 'focus' ? 'Focus Time' :
                   selectedMetric === 'apps' ? 'Application Usage' :
                   'Work Type Distribution'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Last API call:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '12px' }}>
                  {timezoneService.formatUserTime(
                    // eslint-disable-next-line no-restricted-syntax
                    new Date(), 
                    { hour: '2-digit', minute: '2-digit' }
                  )} ({timeRange})
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Integrated Token Usage */}
          <TokenUsage timeRange={timeRange} />

          {currentData.topApps.length === 0 && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffeaa7' }}>
              <Typography variant="body2" sx={{ color: '#856404' }}>
                💡 To see analytics, go to Screenshot History and start tracking your activities!
              </Typography>
            </Box>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AIAnalytics;
